import dayjs from "dayjs";
import * as advancedFormat from "dayjs/plugin/advancedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { CredentialStatus } from "../core/models/Credential";
import { RequestedAttribute } from "../core/models/Proof";
import { SchemaAttributes } from "../core/models/Schema";

dayjs.extend(advancedFormat.default);
dayjs.extend(timezone);
dayjs.extend(utc);

//#region Credentials
/**
 * Gets status color based on credential status
 * @param status {CredentialStatus} credential status
 */
export const getCredentialStatusColor = (
  status?: CredentialStatus
): "error" | "primary" | "success" | "warning" => {
  switch (status) {
    case CredentialStatus.Accepted:
      return "success" /** green */;
    case CredentialStatus.Revoked:
    case CredentialStatus.Rejected:
      return "error" /** red */;
    case CredentialStatus.Pending:
    case CredentialStatus.Sent:
      return "primary" /** blue */;
    case CredentialStatus.Canceled:
    default:
      return "warning" /** gray */;
  }
};
//#endregion

//#region Users
export const getFullName = (firstName?: string, lastName?: string): string => {
  return `${firstName ?? "NA"}${lastName ? " " : ""}${lastName ?? ""}`;
};
//#endregion

//#region Dates
export enum DateFormat {
  DayOfMonth = "MMM. Do YYYY",
  DayOfMonthTime = "d MMM YYYY, hh:mm A", //21 Mar 2025, 10:30 AM
}
export enum GlobalStatus {
  Pending = "pending",
  Completed = "completed",
  Failed = "failed",
  Expired = "expired",
  Used = "used",
}
/**
 * the timezone to use. If not set, the functions will guess it.
 */
let defaultTimezone: string | undefined = undefined;
export const setDefaultTimezone = (timezone?: string) => {
  defaultTimezone = timezone;
};

/**
 * Transform date from timestamp
 * @param timestamp {string} the timestamp
 */
export const getDateFromTimestamp = (timestamp: string) => {
  return dayjs.unix(+timestamp).toDate();
};

/**
 * Parses a date to a string format
 * @param date {Date} the date
 * @param format {DateFormat} the format
 */
export const getFormattedDate = (date: Date, format: DateFormat) => {
  const tz = defaultTimezone || dayjs.tz.guess();
  return dayjs(date).tz(tz).format(format);
};
//#endregion

//#region Others
/**
 * Gets hex color with opacity
 * @param color {string} hex color
 * @param opacity {number} opacity between 0 and 1
 */
export const colorWithOpacity = (color: string, opacity: number = 1) => {
  const _opacity = Math.round(Math.min(Math.max(opacity, 0), 1) * 255);
  return color + _opacity.toString(16).toUpperCase();
};
//#endregion

//#region Invitation
/**
 * Gets status color based on invitation or presentation status
 * @param status {GlobalStatus}  status
 */
export const getStatusColor = (
  status?: GlobalStatus
): "error" | "primary" | "success" | "warning" => {
  switch (status) {
    case GlobalStatus.Used:
      return "success" /** blue */;
    case GlobalStatus.Failed:
    case GlobalStatus.Expired:
      return "error" /** red */;
    case GlobalStatus.Completed:
      return "primary" /** green */;
    case GlobalStatus.Pending:
    default:
      return "warning" /** gray */;
  }
};
//#endregion

//#region Email
/**
 * Validates if the given string is a properly formatted email address.
 * @param email {string} The email address to validate.
 * @returns {boolean} Returns true if the email is valid, otherwise false.
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};
//#endregion

/**
 * Calculates the total number of restrictions across all requested attributes.
 * @param data {RequestedAttribute[]} The array of requested attributes.
 * @returns {number} The total count of restrictions across all attributes.
 */
//#region RequestedAttribute
export const getTotalRestrictions = (data: RequestedAttribute[]) => {
  if (!Array.isArray(data)) return 0; // Handle null or undefined cases
  const totalRestrictions = data?.reduce(
    (sum, attr) => sum + (attr.restrictions?.length || 0),
    0
  );
  return totalRestrictions;
};
//#endregion
/**
 * Sorts schema attributes based on their order in the `previewOrder` array.
 *
 * @param {SchemaAttributes} attributes - The list of schema attributes to be sorted.
 * @param {string[]} previewOrder - An array defining the desired order of attribute names.
 * @returns {SchemaAttributes} - A new sorted array of attributes.
 *
 * Sorting Logic:
 * - Attributes listed in `previewOrder` appear first, in the same order as in `previewOrder`.
 * - Attributes not in `previewOrder` retain their original relative order but are pushed to the end.
 */
export const sortAttributesByPreview = (
  attributes: SchemaAttributes,
  previewOrder: string[]
) => {
  return [...attributes].sort((a, b) => {
    const aIndex = previewOrder.indexOf(a.name);
    const bIndex = previewOrder.indexOf(b.name);

    if (aIndex === -1 && bIndex === -1) return 0; // Both not previewed, keep original order
    if (aIndex === -1) return 1; // `a` is not previewed, push it down
    if (bIndex === -1) return -1; // `b` is not previewed, push it down
    return aIndex - bIndex; // Sort based on preview order
  });
};
/**
 * Capitalizes the first letter of a given string.
 *
 * @param {string} string - The input string.
 * @returns {string} - The string with the first letter capitalized.
 */
export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
/**
 * Capitalizes the first letter of each word in a string.
 *
 * @param str - The input string to capitalize.
 * @returns The string with the first letter of each word capitalized.
 */
export const capitalizeWords = (str: string): string => {
  return str
    .split(" ") // Split string into words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
    .join(" "); // Join words back together
};

/**
 * Replaces all underscores (_) and dashes (-) in a string with spaces.
 *
 * Useful for formatting identifiers like "user_name-id" into more readable labels,
 * such as "user name id".
 *
 * @param str - The input string to normalize.
 * @returns The normalized string with _ and - replaced by spaces.
 */
export const normalizeLabel = (str: string): string => {
  return str.replace(/[_-]/g, " ");
};
/**
 * Normalizes a label by replacing all underscores (_) and dashes (-) with spaces,
 * and capitalizing the first letter of the result.
 *
 * Useful for turning identifiers like "user_name-id" into "User name id".
 *
 * @param str - The input string to normalize and format.
 * @returns The formatted string with spaces and capitalized first letter.
 */
export const formatLabel = (str: string): string => {
  const normalized = normalizeLabel(str);
  return capitalizeFirstLetter(normalized);
};
/**
 * Normalizes a label by replacing all underscores (_) and dashes (-) with spaces,
 * and capitalizing the first letter for all words in the result.
 *
 * Useful for turning identifiers like "user_name-id" into "User Name Id".
 *
 * @param str - The input string to normalize and format.
 * @returns The formatted string with spaces and capitalized first letter for all words.
 */
export const formatLabelWithCapitalAllWords = (str: string): string => {
  const normalized = normalizeLabel(str);
  return capitalizeWords(normalized);
};
